import { AxiosError } from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import { NotificationTypes } from '../../../../../components/Notification/notificationEnums';
import HttpStatus from '../../../../../enums/httpStatus';
import type { AiAgentPayload } from '../../../../../types';
import { superAiAgentsReq } from '../../../../../services/requests';
import { CreateOrEditAiAgentModalProps } from '../../types';

const AI_AGENT_TYPE_OPENAI_CHAT_GPT = 'OPENAI_CHAT_GPT';

const CreateOrEditAiAgentModal = ({
  showModal,
  closeModal,
  callback,
  tenants,
  selectedAiAgent,
}: CreateOrEditAiAgentModalProps) => {
  const { t } = useTranslation();

  const [tenantId, setTenantId] = useState<string>('');
  const [url, setUrl] = useState('');
  const [active, setActive] = useState<'true' | 'false'>('true');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedAiAgent) {
      setTenantId(String(selectedAiAgent.tenant_id));
      setUrl(selectedAiAgent.data.url ?? '');
      setActive(selectedAiAgent.active ? 'true' : 'false');
      return;
    }

    if (tenants.length > 0) {
      setTenantId(String(tenants[0].id));
    }
    setUrl('');
    setActive('true');
  }, [selectedAiAgent, tenants]);

  const handleSubmit = async () => {
    if (!tenantId || !url.trim()) {
      return;
    }

    setIsLoading(true);

    const payload: AiAgentPayload = {
      tenant_id: Number(tenantId),
      type: AI_AGENT_TYPE_OPENAI_CHAT_GPT,
      active: active === 'true',
      data: {
        url: url.trim(),
      },
    };

    try {
      const response = selectedAiAgent
        ? await superAiAgentsReq.update(payload, selectedAiAgent.id)
        : await superAiAgentsReq.store(payload);

      if (![HttpStatus.OK, HttpStatus.CREATED].includes(response.status)) {
        throw response;
      }

      frontendNotification({
        message: selectedAiAgent
          ? t('successfullyUpdatedAiAgent')
          : t('successfullyRegisteredAiAgent'),
        type: NotificationTypes.SUCCESS,
      });

      callback();
      closeModal();
    } catch (err) {
      backendErrorNotification(err as AxiosError<any, any>);
    }

    setIsLoading(false);
  };

  return (
    <Modal
      title={t(selectedAiAgent ? 'editAiAgent' : 'registerAiAgent')}
      showModal={showModal}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={handleSubmit}
      isLoading={isLoading}
      disableActionBtn={!tenantId || !url.trim()}
    >
      <div className="row">
        <div className="inputWithLabel">
          <p className="label">{t('tenantName')}</p>
          <select
            className="select"
            value={tenantId}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => setTenantId(event.target.value)}
          >
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row">
        <div className="inputWithLabel">
          <p className="label">{t('gptUrl')}</p>
          <input
            className="input"
            type="url"
            value={url}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setUrl(event.target.value)}
            placeholder="https://chatgpt.com/g/..."
          />
          <p className="description">{t('aiAgentBrowserEmbedWarning')}</p>
        </div>
      </div>

      <div className="row">
        <div className="inputWithLabel">
          <p className="label">{t('aiAgent')}</p>
          <input className="input" type="text" value={t('openAiChatGpt')} disabled />
        </div>
      </div>

      <div className="row">
        <div className="inputWithLabel">
          <p className="label">{t('active')}</p>
          <select
            className="select"
            value={active}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              setActive(event.target.value as 'true' | 'false');
            }}
          >
            <option value="true">{t('aiAgentEnabled')}</option>
            <option value="false">{t('aiAgentDisabled')}</option>
          </select>
        </div>
      </div>
    </Modal>
  );
};

export default CreateOrEditAiAgentModal;